import QRCode from "qrcode";

// QR value contract — IDENTICAL to mobile (src/app/(member)/card.tsx): `HSC-<member.id>-<tier>`.
export const memberQrValue = (member) => `HSC-${member.id}-${member.tier}`;

const STATUS_COLORS = { ACTIVE: "#7CD992", EXPIRED: "#FF8080", INACTIVE: "#8E8E93" };

// Derive a single membership summary from the member's per-category memberships (real DB state).
export function membershipSummary(memberships = []) {
  const active = memberships.filter((m) => m.status === "valid" || m.status === "expiring");
  if (active.length) {
    const validUntil = active.map((m) => m.expires_at).sort().slice(-1)[0];
    return { status: "ACTIVE", color: STATUS_COLORS.ACTIVE, validUntil, categories: active.map((m) => m.category) };
  }
  if (memberships.length) {
    const sorted = [...memberships].sort((a, b) => (a.expires_at < b.expires_at ? 1 : -1));
    return { status: "EXPIRED", color: STATUS_COLORS.EXPIRED, validUntil: sorted[0].expires_at, categories: sorted.map((m) => m.category) };
  }
  return { status: "INACTIVE", color: STATUS_COLORS.INACTIVE, validUntil: null, categories: [] };
}

export const typeLabel = (summary) => (summary.categories.length ? summary.categories.join(" · ") + " Membership" : "Non-Membership");

const fmtDateID = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};

export function memberCardFilename(member) {
  const name = (member.name || "Member").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const idPart = String(member.id || "").replace(/[^a-zA-Z0-9]+/g, "").slice(0, 12).toUpperCase();
  return `HSC-Member-${name}-${idPart}.png`;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Fixed-dimension (1080×1350) PNG export — independent of viewport. Crisp, scannable QR with quiet zone.
export async function downloadMemberCardPng(member, memberships = []) {
  const W = 1080, H = 1350;
  const summary = membershipSummary(memberships);
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background + border
  ctx.fillStyle = "#08080A"; ctx.fillRect(0, 0, W, H);
  roundRect(ctx, 20, 20, W - 40, H - 40, 44);
  ctx.fillStyle = "#0C0C0F"; ctx.fill();
  ctx.lineWidth = 3; ctx.strokeStyle = "#26262B"; ctx.stroke();

  const cx = W / 2;

  // Header brand — load the real HSC logo (white, transparent bg) and draw proportionally.
  const logo = new Image();
  logo.crossOrigin = "anonymous";
  logo.src = `${window.location.origin}/hsc-logo.png`;
  await new Promise((res) => { logo.onload = res; logo.onerror = res; });
  if (logo.width && logo.height) {
    const lh = 132, lw = (lh * logo.width) / logo.height;
    ctx.drawImage(logo, 72, 84, lw, lh);
  }

  // Tier (top-right)
  ctx.textAlign = "right";
  ctx.fillStyle = "#C7CBD1";
  ctx.font = "700 30px Arial, sans-serif";
  ctx.fillText(String(member.tier || "").toUpperCase(), W - 72, 150);
  ctx.fillStyle = "#6E6E73";
  ctx.font = "600 20px Arial, sans-serif";
  ctx.fillText("TIER", W - 72, 186);

  // Member name
  ctx.textAlign = "center";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 62px Georgia, serif";
  const name = member.name || "Member";
  ctx.fillText(name.length > 22 ? name.slice(0, 21) + "…" : name, cx, 300);

  // Membership type + status pill
  ctx.fillStyle = "#A1A1A6";
  ctx.font = "500 26px Arial, sans-serif";
  ctx.fillText(typeLabel(summary), cx, 348);

  const pillText = summary.status;
  ctx.font = "800 24px Arial, sans-serif";
  const pw = ctx.measureText(pillText).width + 64;
  const px = cx - pw / 2, py = 372;
  roundRect(ctx, px, py, pw, 52, 26);
  ctx.fillStyle = summary.color + "22"; ctx.fill();
  ctx.fillStyle = summary.color;
  ctx.fillText(pillText, cx, py + 35);

  // QR (white box + quiet zone)
  const qrCanvas = document.createElement("canvas");
  await QRCode.toCanvas(qrCanvas, memberQrValue(member), {
    margin: 2, width: 560, errorCorrectionLevel: "M",
    color: { dark: "#000000", light: "#FFFFFF" },
  });
  const box = 620, bx = cx - box / 2, by = 470;
  roundRect(ctx, bx, by, box, box, 32);
  ctx.fillStyle = "#FFFFFF"; ctx.fill();
  ctx.drawImage(qrCanvas, bx + 30, by + 30, box - 60, box - 60);

  // Member ID
  ctx.fillStyle = "#6E6E73";
  ctx.font = "600 20px Arial, sans-serif";
  ctx.fillText("MEMBER ID", cx, 1180);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "600 30px 'Courier New', monospace";
  ctx.fillText(String(member.id || "").toUpperCase(), cx, 1220);

  // Valid until
  ctx.fillStyle = "#6E6E73";
  ctx.font = "600 20px Arial, sans-serif";
  ctx.fillText(summary.status === "EXPIRED" ? "EXPIRED" : "VALID UNTIL", cx, 1270);
  ctx.fillStyle = summary.status === "INACTIVE" ? "#8E8E93" : summary.color;
  ctx.font = "700 30px Arial, sans-serif";
  ctx.fillText(summary.validUntil ? fmtDateID(summary.validUntil) : "—", cx, 1310);

  await new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = memberCardFilename(member); a.click();
      URL.revokeObjectURL(url);
      resolve();
    }, "image/png");
  });
}

export { fmtDateID };
